import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "amounts-container"
}
const _hoisted_2 = ["id", "value", "disabled", "onClick"]
const _hoisted_3 = ["for"]
const _hoisted_4 = {
  key: 0,
  class: "relative"
}
const _hoisted_5 = { class: "custom-amt rd-relative" }
const _hoisted_6 = ["id", "disabled", "placeholder"]
const _hoisted_7 = {
  key: 0,
  class: "custom-amt-check"
}
const _hoisted_8 = {
  key: 1,
  class: "custom-amt rd-relative"
}
const _hoisted_9 = ["id", "disabled", "placeholder"]
const _hoisted_10 = {
  key: 0,
  class: "custom-amt-check"
}

import { GiftArrayOption, IDonationBlockComponent, PageDesign } from "@/builder/interfaces/index";
import { useRenderStore } from "@/builder/stores/renderStore";
import { generateUID } from "@/builder/BuilderUtilities";
import { MaskInputOptions } from "maska";
import { vMaska } from "maska/vue";
import { Ref, computed, inject, onMounted, reactive, ref, watch } from "vue";
import { FieldStyle, GiftArrayType, PromptDecision, PromptType, SmartGiftArrayBasis } from "../../enums";
import { formatCurrency } from "@/utilities/StringUtilities";
import { amountToNumber } from "@/utilities/NumberUtilities";

// injects

export default /*@__PURE__*/_defineComponent({
  __name: 'GiftArrayComponent',
  props: {
    component: {},
    projectId: {},
    projectName: {},
    useSmartGiftArray: { type: Boolean },
    hasGiftArray: { type: Boolean },
    hasCustomAmount: { type: Boolean },
    customAmountLabel: {}
  },
  setup(__props: any) {

const renderStore = useRenderStore();

const pageDesign = inject("pageDesign") as Ref<PageDesign>;
const fieldStyle = computed(() => {
    return pageDesign.value.fieldStyle;
});

// props
const props = __props;

//refs
const formatCustomAmountTimeout = ref(null) as Ref<NodeJS.Timeout | null>;
const customAmount = ref("");
const isFocused = ref(false);
const smartGiftArray = ref<GiftArrayOption[]>([]);

const maskaOptions = reactive<MaskInputOptions>({
    mask: "0.99",
    tokens: {
        0: { pattern: /[0-9]/, multiple: true },
        9: { pattern: /[0-9]/, optional: true },
    },
    preProcess: (val) => val.replace(/[$,]/g, ""),
    postProcess: (val) => postProcessing(val),
});

const postProcessing = (val) => {
    if (!val) return "";
    const sub = 3 - (val.includes(".") ? val.length - val.indexOf(".") : 0);

    return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    })
        .formatToParts(val)
        .map((v) => v.value)
        .join("")
        .slice(0, sub ? -sub : undefined)
        .replace("$", "");
};

// computed
const icon = computed(() => {
    const customFloat = parseFloat(customAmount.value);
    const comparedAmt = giftArray.value.some((arr) => parseFloat(arr.value) === customFloat);

    return !comparedAmt && customFloat > 0 ? "fa-solid fa-check" : "";
});

const giftArray = computed(() => {
    if (showRecurringArray.value && props.component.customData.recurringArray.length > 0)
        return props.component.customData.recurringArray;

    if (showSmartGiftArray.value && smartGiftArray.value.length > 0)
        return smartGiftArray.value;

    return props.component.customData.giftArray;
});

const giftArrayName = computed(() => {
    if (props.projectId) return `giftArray_${props.projectId}`;
    return "giftArrayAmount";
});

const customAmountId = computed(() => {
    if (props.projectId) return `customAmount_${props.projectId}`;
    return "customAmount";
});

const showGiftArray = computed(() => {
    if (props.projectId && !props.component.customData.projectSettings.allowMultipleProjects) {
        if (!props.hasGiftArray && !showCustomAmount.value) {
            return true; //if both gift array and custom amount are hidden, show gift array
        }

        return props.hasGiftArray;
    }

    if (
        props.component.customData.giftArrayType !== GiftArrayType.GiftArray &&
        props.component.customData.giftArrayType !== GiftArrayType.GiftArray_OtherAmount
    )
        return false;

    return true;
});

const showRecurringArray = computed(() => {
    if (!props.component.customData.useRecurringArray) return false;
    return renderStore.isRecurring;
});

const showCustomAmount = computed(() => {
    if (props.projectId && !props.component.customData.projectSettings.allowMultipleProjects) {
        if (!props.hasCustomAmount && !showGiftArray.value) {
            return true; //if both gift array and custom amount are hidden, show custom amount
        }

        return props.hasCustomAmount;
    }

    return (
        props.component.customData.giftArrayType === GiftArrayType.GiftArray_OtherAmount ||
        props.component.customData.giftArrayType === GiftArrayType.CustomAmount ||
        showSmartGiftArray.value
    );
});

const visitorGiving = computed(() => renderStore.visitorGiving);

const validSmartGiftArray = computed(() => {
    //check if there are any options and the values are not over 1000 and above 25
    return (
        smartGiftArray.value.length > 0 &&
        smartGiftArray.value.every((x) => parseInt(x.value) <= 1000 && parseInt(x.value) >= 25)
    );
});

const showSmartGiftArray = computed(() => {
    return (
        !showRecurringArray.value &&
        props.useSmartGiftArray &&
        validSmartGiftArray.value &&
        visitorGiving.value !== null
    );
});

const disableInputs = computed(() => renderStore.usingPayPal);

const acceptedRecurringPrompt = computed(() => {
    return renderStore.page.prompts.filter((x) => x.promptType === PromptType.RecurringAsk && x.promptDecision === PromptDecision.Accept).length > 0;
});

// methods
const setCustomAmount = () => {
    if (customAmount.value === "") {
        return;
    }

    setAmount(customAmount.value);
};

const setAmount = (value: string, isArrayAmount: boolean = false) => {
    renderStore.setAmount(
        value,
        props.projectId,
        props.projectName,
        props.component.customData.projectSettings.allowMultipleProjects
    );

    if (isArrayAmount) {
        formatCustomAmount();
    }
};

const formatCustomAmount = () => {
    if (formatCustomAmountTimeout.value) clearTimeout(formatCustomAmountTimeout.value);

    formatCustomAmountTimeout.value = setTimeout(() => {
        customAmount.value = props.projectId
            ? formatCurrency(renderStore.getAmount(props.projectId))
            : formatCurrency(renderStore.amount.toString());
    }, 100);
};

const formatGiftArray = (value: string | number) => {
    const amount = amountToNumber(value.toString()).toString();
    const [integerPart, decimalPart] = amount.split(".");

    //round decimal part to 2 digits
    decimalPart && decimalPart.length > 2 ? decimalPart.slice(0, 2) : decimalPart;

    if (decimalPart && decimalPart !== "00") {
        return formatCurrency(amount);
    }

    return integerPart;
};

const setGiftArrayLayout = (numOfGifts: number) => {
    return {
        "layout-row-1-col": numOfGifts == 1,
        "layout-row-2-cols": numOfGifts == 2,
        "layout-row-2-cols md:layout-row-3-cols": numOfGifts == 3,
        "layout-row-2-cols md:layout-row-4-cols": numOfGifts >= 4,
    };
};

const buildSmartGiftArray = () => {
    if (!visitorGiving.value) return;

    let basis = 0;
    switch (props.component.customData.smartGiftArraySettings.smartArrayBasis) {
        case SmartGiftArrayBasis.AverageGiftAmount:
            basis = visitorGiving.value.averageGiftAmount;
            break;
        case SmartGiftArrayBasis.LastGiftAmount:
            basis = visitorGiving.value.lastGiftAmount;
            break;
        case SmartGiftArrayBasis.SuggestedAmount:
            basis = visitorGiving.value.suggestedGiftAmount;
            break;
        default:
            basis = visitorGiving.value.suggestedGiftAmount;
            break;
    }

    if (!props.component.customData.smartGiftArraySettings.giftArray) return;

    const arrayMultipliers = props.component.customData.smartGiftArraySettings.giftArray;

    let options = arrayMultipliers
        .map((x) => {
            return {
                value: (basis * x.multiplier).toString(),
                isSelected: x.isSelected,
                isDefault: x.isDefault,
            } as GiftArrayOption;
        });

    //round all options up to the nearest 5
    options.forEach((x) => {
        x.value = (Math.ceil(parseInt(x.value) / 5) * 5).toString();
    });

    //remove any duplicates
    options = options.filter((x, i, self) => self.findIndex((t) => t.value === x.value) === i);

    //if there are not the correct amount of options, double the largest until there are the correct count
    const optionCount = options.length;
    const requiredCount = arrayMultipliers.length;

    if (optionCount < requiredCount) {
        const largest = options.reduce((prev, current) =>
            parseInt(prev.value) > parseInt(current.value) ? prev : current
        );

        for (let i = 0; i < requiredCount - optionCount; i++) {
            options.push({
                id: generateUID('giftArrayOption_'),
                value: (parseInt(largest.value) * 2).toString(),
                isSelected: false,
                isDefault: false,
            });
        }
    }

    smartGiftArray.value = options;
};

const setDefaultAmount = () => {
    //if custom amount is empty, set it to the default gift array amount
    if (!props.projectId && giftArray.value.length > 0 && props.component.customData.giftArrayType !== GiftArrayType.FixedAmount && props.component.customData.giftArrayType !== GiftArrayType.CustomAmount) {
        const defaultGiftArray = giftArray.value.find((x) => x.isDefault) ?? null;

        if (defaultGiftArray) {
            renderStore.setAmount(
                defaultGiftArray.value,
                props.projectId,
                props.projectName,
                props.component.customData.projectSettings.allowMultipleProjects
            );
        }
    }
};

const initializeSmartGiftArray = () => {
    if (props.useSmartGiftArray) {
        buildSmartGiftArray();
    }
};

const handleCurrentAmount = () => {
    const currentAmount = amountToNumber(renderStore.getAmount(props.projectId));

    if (currentAmount > 0 && !renderStore.builderMode) {
        const matchingGiftArray = giftArray.value.find((x) => amountToNumber(x.value) === currentAmount);

        if (matchingGiftArray) {
            matchingGiftArray.isSelected = true;
        } else {
            if (!showCustomAmount.value) {
                addCurrentAmountToGiftArray(currentAmount);
            } else {
                customAmount.value = renderStore.getAmount(props.projectId);
            }
        }
    }
};

const addCurrentAmountToGiftArray = (currentAmount) => {
    giftArray.value.push({
        id: generateUID('giftArrayOption_'),
        value: currentAmount.toString(),
        isSelected: false,
        isDefault: false,
    });

    giftArray.value.sort((a, b) => amountToNumber(a.value) - amountToNumber(b.value));
};

const handleDefaultAmount = () => {
    if (!props.projectId && renderStore.totalAmount === 0) {
        setDefaultAmount();
    }
};

// lifecycle
onMounted(() => {
    initializeSmartGiftArray();
    handleCurrentAmount();
    handleDefaultAmount();
});

// watchers
watch(() => renderStore.amount, (val: number) => {
    if (props.projectId) return;

    const customAmountNumber = amountToNumber(customAmount.value);

    const matchingGiftArray = giftArray.value.find((x) => amountToNumber(x.value) === val);
    if (customAmountNumber !== val && !matchingGiftArray) {
        customAmount.value = formatCurrency(val.toString());
    }
});

watch(() => renderStore.gift.projectSplits, (newVal) => {
    if (!props.projectId) return;

    if (newVal.findIndex(x => x.projectId == props.projectId) == -1) {
        customAmount.value = "";
    }

    if (renderStore.getAmount(props.projectId) == "0") {
        customAmount.value = "";
    }
}, { deep: true });

watch(() => renderStore.visitorGiving, (val) => {
    if (props.useSmartGiftArray && val !== null) {
        buildSmartGiftArray();
        setDefaultAmount();
    }
});

watch(showRecurringArray, (val: boolean) => {
    if (val) {
        if (acceptedRecurringPrompt.value) {
            return;
        }

        setDefaultAmount();
    } else {
        setDefaultAmount();
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (showGiftArray.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
          _createElementVNode("ul", {
            class: _normalizeClass(["layout-row-cols rd-gap-y-2 -rd-mx-1", setGiftArrayLayout(giftArray.value?.length ?? 0)])
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(giftArray.value, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: 'giftArray_' + item.value
              }, [
                _createElementVNode("button", {
                  id: giftArrayName.value + '_' + item.value,
                  value: item.value,
                  class: _normalizeClass([`rd-btn rd-btn-primary ${_ctx.component.customData.buttonSize || ''} rd-w-full`,
                        { active: _unref(renderStore).builderMode ? item.isDefault : _unref(renderStore).getAmount(_ctx.projectId).replace('.00', '') === item.value.toString() }
                        ]),
                  style: _normalizeStyle({
                            borderRadius: _ctx.component.styles.borderRadius + 'px !important' || '',
                            borderWidth: _ctx.component.styles.outlineWeight + 'px !important' || '',
                            borderColor: _ctx.component.styles.outlineColor + ' !important' || '',
                            width: _ctx.component.styles.fullWidth ? '100%' : '',
                            '--button-text-color': _ctx.component.styles.fontColor || '',
                            '--button-hover-color': _ctx.component.styles.hoverColor || '',
                            '--button-hover-text-color': _ctx.component.styles.hoverTextColor || '',
                            '--button-color': _ctx.component.styles.backgroundColor || '',
                        }),
                  type: "button",
                  disabled: disableInputs.value,
                  onClick: ($event: any) => (setAmount(item.value, true))
                }, [
                  _createElementVNode("i", {
                    class: _normalizeClass(["fa-solid fa-check rd-me-1", { active: _unref(renderStore).getAmount(_ctx.projectId).replace('.00', '') === item.value }]),
                    "aria-hidden": "true"
                  }, null, 2),
                  _createTextVNode("$" + _toDisplayString(formatGiftArray(item.value)), 1)
                ], 14, _hoisted_2)
              ]))
            }), 128))
          ], 2)
        ]))
      : _createCommentVNode("", true),
    (showCustomAmount.value)
      ? (_openBlock(), _createElementBlock("section", {
          key: 1,
          class: _normalizeClass(["custom-amt-container rd-mt-4", [{ 'form-underline': fieldStyle.value == _unref(FieldStyle).Underline },
        { 'form-compact': fieldStyle.value == _unref(FieldStyle).Compact }]])
        }, [
          _createElementVNode("label", {
            for: customAmountId.value,
            class: "rd-sr-only"
          }, _toDisplayString(_ctx.customAmountLabel ? _ctx.customAmountLabel : 'Donation Amount'), 9, _hoisted_3),
          (fieldStyle.value == _unref(FieldStyle).Underline)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _cache[7] || (_cache[7] = _createElementVNode("span", { class: "currency" }, "$", -1)),
                  _withDirectives(_createElementVNode("input", {
                    id: customAmountId.value,
                    disabled: disableInputs.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((customAmount).value = $event)),
                    onKeyup: setCustomAmount,
                    onChange: formatCustomAmount,
                    onFocus: _cache[1] || (_cache[1] = ($event: any) => (isFocused.value = true)),
                    onBlur: _cache[2] || (_cache[2] = ($event: any) => (isFocused.value = false)),
                    type: "text",
                    autocomplete: "off",
                    placeholder: _ctx.component.customData.customAmountPlaceHolder ?? undefined
                  }, null, 40, _hoisted_6), [
                    [_vModelText, customAmount.value],
                    [_unref(vMaska), maskaOptions]
                  ]),
                  (icon.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_7, _cache[6] || (_cache[6] = [
                        _createElementVNode("svg", {
                          xmlns: "http://www.w3.org/2000/svg",
                          viewBox: "0 0 24 24",
                          fill: "currentColor",
                          "aria-hidden": "true"
                        }, [
                          _createElementVNode("path", { d: "M9.9997 15.1709L19.1921 5.97852L20.6063 7.39273L9.9997 17.9993L3.63574 11.6354L5.04996 10.2212L9.9997 15.1709Z" })
                        ], -1)
                      ])))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("span", {
                  class: _normalizeClass(["border-input-focus", { active: isFocused.value }])
                }, null, 2)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _cache[9] || (_cache[9] = _createElementVNode("span", { class: "currency" }, "$", -1)),
                _withDirectives(_createElementVNode("input", {
                  id: customAmountId.value,
                  disabled: disableInputs.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((customAmount).value = $event)),
                  onKeyup: setCustomAmount,
                  onChange: formatCustomAmount,
                  onFocus: _cache[4] || (_cache[4] = ($event: any) => (isFocused.value = true)),
                  onBlur: _cache[5] || (_cache[5] = ($event: any) => (isFocused.value = false)),
                  type: "text",
                  autocomplete: "off",
                  placeholder: _ctx.component.customData.customAmountPlaceHolder ?? undefined
                }, null, 40, _hoisted_9), [
                  [_vModelText, customAmount.value],
                  [_unref(vMaska), maskaOptions]
                ]),
                (icon.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, _cache[8] || (_cache[8] = [
                      _createElementVNode("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 24 24",
                        fill: "currentColor",
                        "aria-hidden": "true"
                      }, [
                        _createElementVNode("path", { d: "M9.9997 15.1709L19.1921 5.97852L20.6063 7.39273L9.9997 17.9993L3.63574 11.6354L5.04996 10.2212L9.9997 15.1709Z" })
                      ], -1)
                    ])))
                  : _createCommentVNode("", true)
              ]))
        ], 2))
      : _createCommentVNode("", true)
  ], 64))
}
}

})